import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { 
  Send, 
  Bot, 
  User, 
  Sparkles,
  Hash,
  FileText,
  ShoppingBag,
  Lightbulb,
  MessageSquare,
  Plus,
  Trash2,
  Copy,
  Check,
  Loader2,
  Wand2,
  Image as ImageIcon
} from "lucide-react";
import { format } from "date-fns";
import { API } from "../../apiConfig";


// Context type icons and labels
const CONTEXT_TYPES = {
  general: { icon: MessageSquare, label: "General Chat", color: "bg-gray-100 text-gray-800" },
  caption: { icon: Sparkles, label: "Caption Generator", color: "bg-purple-100 text-purple-800" },
  description: { icon: FileText, label: "Art Description", color: "bg-blue-100 text-blue-800" },
  sales: { icon: ShoppingBag, label: "Sales Copy", color: "bg-green-100 text-green-800" },
  ideas: { icon: Lightbulb, label: "Content Ideas", color: "bg-yellow-100 text-yellow-800" },
  hashtags: { icon: Hash, label: "Hashtag Generator", color: "bg-pink-100 text-pink-800" }
};

// Quick action buttons
const QUICK_ACTIONS = [
  { action: "generate_caption", label: "Generate Caption", icon: Sparkles },
  { action: "generate_hashtags", label: "Suggest Hashtags", icon: Hash },
  { action: "generate_description", label: "Write Description", icon: FileText },
  { action: "suggest_ideas", label: "Content Ideas", icon: Lightbulb }
];

const AIAssistant = ({ token, embedded = false, onInsertContent }) => {
  const [sessions, setSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [contextType, setContextType] = useState("general");
  const [isLoading, setIsLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [quickActionLoading, setQuickActionLoading] = useState(null);
  const messagesEndRef = useRef(null);
  
  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [token]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/ai/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(res.data.sessions || []);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    }
  };
  
  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/ai/sessions/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data.messages || []);
      setCurrentSession(sessions.find(s => s.session_id === sessionId));
    } catch (error) {
      toast.error("Failed to load conversation");
    }
  };
  
  const startNewSession = () => {
    setCurrentSession(null);
    setMessages([]);
    setInputMessage("");
  };
  
  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    
    try {
      await axios.delete(`${API}/ai/sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(sessions.filter(s => s.session_id !== sessionId));
      if (currentSession?.session_id === sessionId) {
        startNewSession();
      }
      toast.success("Conversation deleted");
    } catch (error) {
      toast.error("Failed to delete conversation");
    }
  };
  
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage("");
    setIsLoading(true);
    
    // Optimistically add user message
    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);
    
    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: userMessage,
        session_id: currentSession?.session_id,
        context_type: contextType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update with real response
      const assistantMsg = {
        message_id: res.data.message_id,
        role: "assistant",
        content: res.data.response,
        created_at: new Date().toISOString()
      };
      
      setMessages(prev => [...prev.filter(m => m.message_id !== tempUserMsg.message_id), 
        { ...tempUserMsg, message_id: `user_${Date.now()}` },
        assistantMsg
      ]);
      
      // Update session info
      if (!currentSession) {
        setCurrentSession({ session_id: res.data.session_id, title: userMessage.slice(0, 50) });
        fetchSessions();
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to get response");
      setMessages(prev => prev.filter(m => m.message_id !== tempUserMsg.message_id));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleQuickAction = async (action) => {
    setQuickActionLoading(action);
    
    try {
      const res = await axios.post(`${API}/ai/quick`, {
        action,
        context: {}
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add to messages as a new conversation
      setMessages([
        { message_id: `user_${Date.now()}`, role: "user", content: `Quick action: ${action}`, created_at: new Date().toISOString() },
        { message_id: `asst_${Date.now()}`, role: "assistant", content: res.data.result, created_at: new Date().toISOString() }
      ]);
      setCurrentSession(null);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Quick action failed");
    } finally {
      setQuickActionLoading(null);
    }
  };
  
  const copyToClipboard = (text, messageId) => {
    navigator.clipboard.writeText(text);
    setCopiedId(messageId);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success("Copied to clipboard");
  };
  
  const insertContent = (text) => {
    if (onInsertContent) {
      onInsertContent(text);
      toast.success("Content inserted");
    }
  };
  
  return (
    <div className={`flex ${embedded ? "h-[500px]" : "h-[calc(100vh-200px)]"} gap-4`}>
      {/* Sidebar - Sessions */}
      {!embedded && (
        <div className="w-64 border-r pr-4 flex flex-col">
          <Button onClick={startNewSession} className="w-full mb-4">
            <Plus className="h-4 w-4 mr-2" />
            New Chat
          </Button>
          
          <div className="flex-1 overflow-y-auto space-y-2">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => loadSession(session.session_id)}
                className={`p-3 rounded-lg cursor-pointer transition-colors group ${
                  currentSession?.session_id === session.session_id
                    ? "bg-primary/10 border border-primary/20"
                    : "hover:bg-muted"
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{session.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(session.updated_at), "MMM d, h:mm a")}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                    onClick={(e) => deleteSession(session.session_id, e)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
            
            {sessions.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No conversations yet
              </p>
            )}
          </div>
        </div>
      )}
      
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold">First Nations Art Assistant</h3>
              <p className="text-xs text-muted-foreground">Powered by Gemini AI</p>
            </div>
          </div>
          
          <Select value={contextType} onValueChange={setContextType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CONTEXT_TYPES).map(([key, { icon: Icon, label }]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    {label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Quick Actions */}
        {messages.length === 0 && (
          <div className="mb-4">
            <p className="text-sm text-muted-foreground mb-2">Quick Actions</p>
            <div className="grid grid-cols-2 gap-2">
              {QUICK_ACTIONS.map(({ action, label, icon: Icon }) => (
                <Button
                  key={action}
                  variant="outline"
                  className="justify-start"
                  onClick={() => handleQuickAction(action)}
                  disabled={quickActionLoading !== null}
                >
                  {quickActionLoading === action ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Icon className="h-4 w-4 mr-2" />
                  )}
                  {label}
                </Button>
              ))}
            </div>
          </div>
        )}
        
        {/* Messages */}
        <div className="flex-1 overflow-y-auto space-y-4 pr-2">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex p-4 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full mb-4">
                <Wand2 className="h-8 w-8 text-amber-600" />
              </div>
              <h4 className="font-semibold mb-2">How can I help you today?</h4>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                I specialize in First Nations art and fashion. Ask me to write captions, 
                generate descriptions, suggest content ideas, or answer questions about 
                Aboriginal art.
              </p>
            </div>
          )}
          
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.message_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${message.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {message.role === "assistant" && (
                  <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg h-fit">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                
                <div className={`max-w-[80%] ${message.role === "user" ? "order-first" : ""}`}>
                  <div
                    className={`p-3 rounded-lg ${
                      message.role === "user"
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted"
                    }`}
                  >
                    <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                  </div>
                  
                  {message.role === "assistant" && (
                    <div className="flex gap-1 mt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => copyToClipboard(message.content, message.message_id)}
                      >
                        {copiedId === message.message_id ? (
                          <Check className="h-3 w-3 mr-1" />
                        ) : (
                          <Copy className="h-3 w-3 mr-1" />
                        )}
                        Copy
                      </Button>
                      {onInsertContent && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => insertContent(message.content)}
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Insert
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                {message.role === "user" && (
                  <div className="p-2 bg-primary rounded-lg h-fit">
                    <User className="h-4 w-4 text-primary-foreground" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
          
          {isLoading && (
            <div className="flex gap-3">
              <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg h-fit">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-muted p-3 rounded-lg">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        
        {/* Input Area */}
        <div className="pt-4 border-t mt-4">
          <div className="flex gap-2">
            <Textarea
              placeholder="Ask about First Nations art, request captions, descriptions, or content ideas..."
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              className="resize-none"
              rows={2}
            />
            <Button 
              onClick={sendMessage} 
              disabled={!inputMessage.trim() || isLoading}
              className="h-auto"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
